// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, Truck, Package, CreditCard, MapPin, User, Phone, Mail } from 'lucide-react';

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user, customer, refreshCustomer } = useAuth();
  const {
    items, subtotal, discount, deliveryFee, tax, total,
    coupon, fulfillment, setFulfillment, clearCart,
  } = useCart();

  const [step, setStep] = useState(1); // 1: info, 2: review, 3: success
  const [placing, setPlacing] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);

  const [form, setForm] = useState({
    full_name:    customer?.full_name  || '',
    email:        user?.email          || '',
    phone:        customer?.phone      || '',
    address_line1: '',
    address_line2: '',
    city:         '',
    state:        'CA',
    zip:          '',
    notes:        '',
    card_number:  '',
    card_expiry:  '',
    card_cvc:     '',
  });
  const [errors, setErrors] = useState({});

  const set = (field, val) => {
    setForm(f => ({ ...f, [field]: val }));
    setErrors(e => ({ ...e, [field]: '' }));
  };

  const validateStep1 = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = 'Required';
    if (!form.email.trim())     errs.email     = 'Required';
    if (!form.phone.trim())     errs.phone     = 'Required';
    if (fulfillment === 'delivery') {
      if (!form.address_line1.trim()) errs.address_line1 = 'Required';
      if (!form.city.trim())          errs.city          = 'Required';
      if (!form.zip.trim())           errs.zip           = 'Required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs = {};
    // Simulate payment validation — real app would use Stripe
    if (!form.card_number.replace(/\s/g, '') || form.card_number.replace(/\s/g, '').length < 12) errs.card_number = 'Enter a valid card number';
    if (!form.card_expiry || form.card_expiry.length < 5) errs.card_expiry = 'Enter expiry (MM/YY)';
    if (!form.card_cvc || form.card_cvc.length < 3)       errs.card_cvc    = 'Enter CVC';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (!validateStep2()) return;
    setPlacing(true);

    try {
      // Compute loyalty points to award based on tier multiplier
      const multiplier = customer?.grocery_loyalty_tiers?.point_multiplier || 1;
      const pointsEarned = Math.floor((subtotal / 100) * multiplier);

      // Build delivery address JSON
      const deliveryAddress = fulfillment === 'delivery' ? {
        full_name:    form.full_name,
        phone:        form.phone,
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city:          form.city,
        state:         form.state,
        zip:           form.zip,
      } : null;

      // Create order
      const { data: order, error: orderErr } = await supabase
        .from('grocery_orders')
        .insert({
          customer_id:           customer.id,
          order_status:          'PENDING',
          fulfillment_type:      fulfillment.toUpperCase(),
          subtotal_cents:        subtotal,
          discount_cents:        discount,
          delivery_fee_cents:    deliveryFee,
          tax_cents:             tax,
          total_cents:           total,
          loyalty_points_earned: pointsEarned,
          coupon_id:             coupon?.id || null,
          delivery_address:      deliveryAddress,
          notes:                 form.notes || null,
        })
        .select('id, order_number')
        .single();

      if (orderErr) throw orderErr;

      // Insert order items
      const orderItems = items.map(item => ({
        order_id:        order.id,
        product_id:      item.product_id,
        product_name:    item.product_name,
        quantity:        item.quantity,
        unit_price_cents: item.sale_price_cents || item.price_cents,
        total_cents:     (item.sale_price_cents || item.price_cents) * item.quantity,
      }));

      const { error: itemsErr } = await supabase
        .from('grocery_order_items')
        .insert(orderItems);

      if (itemsErr) throw itemsErr;

      // If delivery, create delivery record
      if (fulfillment === 'delivery') {
        await supabase.from('grocery_deliveries').insert({
          order_id:         order.id,
          delivery_status:  'SCHEDULED',
          delivery_address: deliveryAddress,
          estimated_delivery_at: new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString(),
        });
      }

      // Award loyalty points to customer
      if (customer?.id && pointsEarned > 0) {
        await supabase
          .from('grocery_store_customers')
          .update({ loyalty_points: (customer.loyalty_points || 0) + pointsEarned })
          .eq('id', customer.id);
        await refreshCustomer();
      }

      // Clear cart
      await clearCart();

      setOrderId(order.id);
      setOrderNumber(order.order_number);
      setStep(3);
    } catch (err) {
      console.error('Order placement failed:', err);
      setErrors({ submit: 'Failed to place order. Please try again.' });
    } finally {
      setPlacing(false);
    }
  };

  // ─── Step 3: Success ────────────────────────────────────────────
  if (step === 3) {
    return (
      <div className="page-container" style={{ paddingTop: 64, textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#F0FDF4', border: '3px solid #16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <CheckCircle size={40} color="#16A34A" />
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 900, color: '#14532D', marginBottom: 8 }}>Order Placed!</h1>
        <p style={{ fontSize: 16, color: '#64748B', marginBottom: 6 }}>
          Thank you for your order. We're getting it ready now.
        </p>
        <div style={{ background: '#F0FDF4', borderRadius: 14, padding: '16px 24px', display: 'inline-block', marginBottom: 28 }}>
          <div style={{ fontSize: 13, color: '#64748B', marginBottom: 2 }}>Order number</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#14532D' }}>#{orderNumber}</div>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => navigate(`/orders/${orderId}`)}>
            Track Order
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/')}>
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const inputStyle = { width: '100%', minHeight: 42, fontSize: 14 };

  return (
    <div className="page-container" style={{ paddingTop: 32, paddingBottom: 48 }}>
      <h1 style={{ fontSize: 26, fontWeight: 900, color: '#14532D', marginBottom: 28 }}>Checkout</h1>

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32, maxWidth: 400 }}>
        {[
          { n: 1, label: 'Info & Delivery' },
          { n: 2, label: 'Review & Pay' },
        ].map((s, i) => (
          <React.Fragment key={s.n}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', fontWeight: 800, fontSize: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: step >= s.n ? '#16A34A' : '#F0FDF4',
                color: step >= s.n ? '#fff' : '#94A3B8',
                border: step >= s.n ? 'none' : '2px solid #BBF7D0',
              }}>{s.n}</div>
              <div style={{ fontSize: 11, color: step >= s.n ? '#14532D' : '#94A3B8', fontWeight: step >= s.n ? 600 : 400, whiteSpace: 'nowrap' }}>{s.label}</div>
            </div>
            {i === 0 && (
              <div style={{ flex: 1, height: 2, background: step >= 2 ? '#16A34A' : '#BBF7D0', margin: '0 8px', marginBottom: 20 }} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 32, alignItems: 'start' }}>
        {/* Main form */}
        <div>
          {/* Step 1: Contact + Delivery */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Fulfillment */}
              <div className="card" style={{ padding: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#14532D', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Package size={16} /> Fulfillment Method
                </h3>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { key: 'delivery', label: 'Home Delivery', icon: <Truck size={18} />, sub: 'Est. 2–4 hours' },
                    { key: 'pickup',   label: 'In-Store Pickup', icon: <Package size={18} />, sub: 'Ready in 1 hour' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setFulfillment(opt.key)}
                      style={{
                        flex: 1, padding: '12px', borderRadius: 10, cursor: 'pointer',
                        border: fulfillment === opt.key ? '2px solid #16A34A' : '2px solid #E2E8F0',
                        background: fulfillment === opt.key ? '#F0FDF4' : '#fff',
                        display: 'flex', alignItems: 'center', gap: 10,
                        transition: 'all 0.15s',
                      }}
                    >
                      <span style={{ color: fulfillment === opt.key ? '#16A34A' : '#94A3B8' }}>{opt.icon}</span>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: fulfillment === opt.key ? '#14532D' : '#64748B' }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{opt.sub}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact info */}
              <div className="card" style={{ padding: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#14532D', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <User size={16} /> Contact Information
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Full Name *</label>
                    <input className="input" style={inputStyle} value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Jane Smith" />
                    {errors.full_name && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.full_name}</div>}
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Email *</label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                      <input className="input" style={{ ...inputStyle, paddingLeft: 32 }} value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@example.com" type="email" />
                    </div>
                    {errors.email && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.email}</div>}
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Phone *</label>
                    <div style={{ position: 'relative' }}>
                      <Phone size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                      <input className="input" style={{ ...inputStyle, paddingLeft: 32 }} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 000-0000" type="tel" />
                    </div>
                    {errors.phone && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.phone}</div>}
                  </div>
                </div>
              </div>

              {/* Delivery address */}
              {fulfillment === 'delivery' && (
                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#14532D', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MapPin size={16} /> Delivery Address
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Street Address *</label>
                      <input className="input" style={inputStyle} value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="123 Main St" />
                      {errors.address_line1 && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.address_line1}</div>}
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Apt, Suite, Unit (optional)</label>
                      <input className="input" style={inputStyle} value={form.address_line2} onChange={e => set('address_line2', e.target.value)} placeholder="Apt 4B" />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>City *</label>
                        <input className="input" style={inputStyle} value={form.city} onChange={e => set('city', e.target.value)} placeholder="Springfield" />
                        {errors.city && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.city}</div>}
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>State *</label>
                        <select className="input" style={inputStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>ZIP *</label>
                        <input className="input" style={inputStyle} value={form.zip} onChange={e => set('zip', e.target.value)} placeholder="90210" maxLength={10} />
                        {errors.zip && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.zip}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Order notes */}
              <div className="card" style={{ padding: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Order Notes (optional)</label>
                <textarea
                  className="input"
                  style={{ minHeight: 72, resize: 'vertical', fontSize: 13 }}
                  value={form.notes}
                  onChange={e => set('notes', e.target.value)}
                  placeholder="Any special requests or delivery instructions..."
                />
              </div>

              <button className="btn btn-primary btn-lg" onClick={() => { if (validateStep1()) setStep(2); }}>
                Continue to Payment
              </button>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Order review */}
              <div className="card" style={{ padding: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#14532D', marginBottom: 14 }}>Order Review</h3>
                {items.map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F0FDF4', fontSize: 13 }}>
                    <span style={{ color: '#374151' }}>{item.product_name} × {item.quantity}</span>
                    <span style={{ color: '#14532D', fontWeight: 600 }}>{fmt((item.sale_price_cents || item.price_cents) * item.quantity)}</span>
                  </div>
                ))}
                {fulfillment === 'delivery' && (
                  <div style={{ marginTop: 12, padding: '10px 12px', background: '#F0FDF4', borderRadius: 8, fontSize: 12, color: '#64748B' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={12} color="#16A34A" />
                      {form.address_line1}, {form.city}, {form.state} {form.zip}
                    </div>
                  </div>
                )}
              </div>

              {/* Payment */}
              <div className="card" style={{ padding: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#14532D', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CreditCard size={16} /> Payment
                </h3>
                <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 16 }}>Demo mode — no real charges will be made.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Card Number *</label>
                    <input
                      className="input" style={inputStyle}
                      value={form.card_number}
                      onChange={e => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 16);
                        set('card_number', v.replace(/(.{4})/g, '$1 ').trim());
                      }}
                      placeholder="4242 4242 4242 4242"
                      maxLength={19}
                    />
                    {errors.card_number && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.card_number}</div>}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Expiry *</label>
                      <input
                        className="input" style={inputStyle}
                        value={form.card_expiry}
                        onChange={e => {
                          let v = e.target.value.replace(/\D/g, '').slice(0, 4);
                          if (v.length >= 2) v = v.slice(0, 2) + '/' + v.slice(2);
                          set('card_expiry', v);
                        }}
                        placeholder="MM/YY"
                        maxLength={5}
                      />
                      {errors.card_expiry && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.card_expiry}</div>}
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>CVC *</label>
                      <input
                        className="input" style={inputStyle}
                        value={form.card_cvc}
                        onChange={e => set('card_cvc', e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="123"
                        maxLength={4}
                      />
                      {errors.card_cvc && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.card_cvc}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {errors.submit && (
                <div style={{ background: '#FEE2E2', color: '#DC2626', padding: '12px 16px', borderRadius: 10, fontSize: 14 }}>
                  {errors.submit}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-outline" onClick={() => setStep(1)}>← Back</button>
                <button
                  className="btn btn-primary btn-lg"
                  style={{ flex: 1 }}
                  onClick={handlePlaceOrder}
                  disabled={placing}
                >
                  {placing ? 'Placing Order...' : `Place Order · ${fmt(total)}`}
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', color: '#94A3B8', fontSize: 12 }}>
                🔒 Your payment info is encrypted and secure
              </div>
            </div>
          )}
        </div>

        {/* Summary sidebar */}
        <div style={{ position: 'sticky', top: 80 }}>
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#14532D', marginBottom: 16 }}>Order Summary</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B' }}>
                <span>Subtotal</span><span>{fmt(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#16A34A', fontWeight: 600 }}>
                  <span>Discount</span><span>−{fmt(discount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B' }}>
                <span>{fulfillment === 'pickup' ? 'Pickup' : 'Delivery'}</span>
                <span style={{ color: deliveryFee === 0 ? '#16A34A' : undefined }}>
                  {deliveryFee === 0 ? 'Free' : fmt(deliveryFee)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B' }}>
                <span>Tax</span><span>{fmt(tax)}</span>
              </div>
              <div style={{ height: 1, background: '#F0FDF4' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, fontSize: 16, color: '#14532D' }}>
                <span>Total</span><span>{fmt(total)}</span>
              </div>
            </div>

            {/* Loyalty points preview */}
            {customer && (
              <div style={{ marginTop: 16, background: '#F0FDF4', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 12, color: '#16A34A', fontWeight: 700 }}>
                  ⭐ You'll earn {Math.floor((subtotal / 100) * (customer.grocery_loyalty_tiers?.point_multiplier || 1))} points with this order
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
